import { useEffect, useRef, useState } from 'react';

/**
 * Un mapa para ver dónde está una vivienda y para marcarla con un toque.
 *
 * **Necesita internet, y eso hay que decirlo.** Kodica corre en un servidor
 * dentro de la caseta y su promesa es seguir abriendo la reja con el enlace
 * caído; este mapa no puede cumplir esa promesa porque las imágenes las sirve
 * OpenStreetMap desde la red. La línea que se traza es esta: la reja es lo que
 * no puede depender de internet. Esto es una comodidad de una pantalla
 * administrativa que se usa desde una oficina, y **cuando no hay red se calla y
 * deja el campo de coordenadas, que sigue funcionando igual**.
 *
 * Por eso Leaflet se carga solo cuando esta pantalla se abre: el APK del
 * residente no tiene por qué cargar con una librería de mapas que no usa.
 */

interface Props {
  lat: number | null;
  lng: number | null;
  /** Ausente = solo mirar. Presente = tocar el mapa fija la ubicación. */
  alElegir?: (lat: number, lng: number) => void;
}

/** Guadalajara, si no hay nada que enseñar todavía. */
const CENTRO_POR_OMISION: [number, number] = [20.6736, -103.344];

/**
 * El pin, dibujado a mano.
 *
 * Leaflet trae el suyo como PNG dentro del paquete y lo pide por una ruta
 * relativa que el empaquetador no reescribe: la imagen no baja y el navegador
 * dibuja el texto alternativo, un recuadro que dice «Marker». Se ve como si el
 * mapa estuviera roto.
 *
 * Va en SVG dentro del propio HTML por la misma razón que todo lo demás aquí:
 * **no hay archivo que resolver ni petición que hacer**, así que la casa se
 * marca igual con el enlace caído. La punta abajo al centro, que es donde la
 * gente entiende que está señalando.
 */
const PIN_SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="26" height="36" viewBox="0 0 26 36" aria-hidden="true">
  <path d="M13 35S25 21.5 25 13A12 12 0 1 0 1 13c0 8.5 12 22 12 22z" fill="#c62828" stroke="#fff" stroke-width="2"/>
  <circle cx="13" cy="13" r="4.5" fill="#fff"/>
</svg>`;

export function MapaUbicacion({ lat, lng, alElegir }: Props) {
  const contenedor = useRef<HTMLDivElement>(null);
  const mapa = useRef<{
    setView: (c: [number, number], z: number) => void;
    invalidateSize: () => void;
    remove: () => void;
  } | null>(null);
  const marcador = useRef<{ setLatLng: (c: [number, number]) => void } | null>(null);
  /** Pone el pin, creándolo si aún no existe. Se arma al montar, cuando ya hay
   *  Leaflet cargado, y lo usan tanto el clic como la llegada de coordenadas. */
  const ponerPin = useRef<((la: number, ln: number) => void) | null>(null);
  const [falloRed, setFalloRed] = useState(false);
  const [listo, setListo] = useState(false);

  useEffect(() => {
    let vivo = true;
    let observador: ResizeObserver | undefined;

    void (async () => {
      // Importación diferida: así Leaflet no entra en el paquete principal ni,
      // por tanto, en el APK del residente.
      const L = await import('leaflet');
      await import('leaflet/dist/leaflet.css');
      if (!vivo || !contenedor.current || mapa.current) return;

      const centro: [number, number] = lat != null && lng != null ? [lat, lng] : CENTRO_POR_OMISION;
      const m = L.map(contenedor.current).setView(centro, lat != null ? 17 : 12);

      // Dos vistas, y la aérea manda.
      //
      // Marcar una casa en el plano de calles es adivinar: en un
      // fraccionamiento todas las manzanas son rectángulos iguales y la casa 14
      // no se distingue de la 16. En la foto aérea se ve el techo, la cochera y
      // la alberca, que es como alguien reconoce de verdad un domicilio. Por eso
      // abre en satélite; quien prefiera los nombres de calle cambia arriba a la
      // derecha.
      //
      // Las imágenes son de Esri y no piden llave. El plano sigue siendo
      // OpenStreetMap.
      const satelite = L.tileLayer(
        'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
        { maxZoom: 19, attribution: 'Imágenes © Esri' },
      );
      const calles = L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
        maxZoom: 19,
        attribution: '© OpenStreetMap',
      });
      // Si las imágenes no bajan —sin internet, o el proveedor caído— se dice y
      // se deja de estorbar, en vez de enseñar un cuadro gris sin explicación.
      satelite.on('tileerror', () => vivo && setFalloRed(true));
      calles.on('tileerror', () => vivo && setFalloRed(true));
      satelite.addTo(m);
      L.control.layers({ Satélite: satelite, Calles: calles }).addTo(m);

      // `className` propio sustituye al `leaflet-div-icon` de fábrica, que
      // pinta un cuadrito blanco con borde detrás del dibujo.
      const icono = L.divIcon({
        className: 'kodica-pin',
        html: PIN_SVG,
        iconSize: [26, 36],
        iconAnchor: [13, 36],
      });

      ponerPin.current = (la, ln) => {
        if (marcador.current) marcador.current.setLatLng([la, ln]);
        else
          marcador.current = L.marker([la, ln], { icon: icono }).addTo(
            m,
          ) as unknown as typeof marcador.current;
      };

      if (lat != null && lng != null) ponerPin.current(lat, lng);

      if (alElegir) {
        m.on('click', (e: { latlng: { lat: number; lng: number } }) => {
          const { lat: la, lng: ln } = e.latlng;
          ponerPin.current?.(la, ln);
          // Seis decimales son ~11 cm: de sobra para una casa, y no arrastra la
          // basura de precisión que trae el clic.
          alElegir(Number(la.toFixed(6)), Number(ln.toFixed(6)));
        });
      }

      mapa.current = m as unknown as typeof mapa.current;
      setListo(true);

      // El mapa vive dentro de un «Domicilio y ubicación» plegado, y un
      // `<details>` cerrado es `display:none`: Leaflet se monta creyendo que
      // mide cero, pide tres mosaicos y deja el resto del recuadro en gris
      // aunque después se abra. Nadie lo relaciona con el plegado —se ve como
      // un mapa roto—, así que se vigila el tamaño del contenedor y se le
      // vuelve a medir en cuanto aparece.
      observador = new ResizeObserver(() => m.invalidateSize());
      if (contenedor.current) observador.observe(contenedor.current);
    })();

    return () => {
      vivo = false;
      observador?.disconnect();
      mapa.current?.remove();
      mapa.current = null;
      marcador.current = null;
      ponerPin.current = null;
    };
    // Solo al montar: mover el mapa con cada tecla del campo de coordenadas
    // haría saltar la vista mientras alguien escribe.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Recentrar cuando las coordenadas llegan o cambian desde fuera.
  //
  // **Crea el pin si no había ninguno**, y no solo lo mueve. En el editor del
  // padrón el mapa se monta antes de que el formulario se llene, así que al
  // abrir una vivienda ya ubicada las coordenadas llegan siempre después: mover
  // un marcador inexistente no hacía nada y la casa salía sin señalar.
  useEffect(() => {
    if (!listo || lat == null || lng == null) return;
    mapa.current?.setView([lat, lng], 17);
    ponerPin.current?.(lat, lng);
  }, [lat, lng, listo]);

  return (
    <div>
      <div
        ref={contenedor}
        className="h-64 w-full overflow-hidden rounded-xl border border-outline-variant"
        role="application"
        aria-label="Mapa de la ubicación de la vivienda"
      />
      {falloRed ? (
        <p className="mt-2 text-label-sm text-on-surface-variant">
          El mapa necesita internet y no hay. Escribe las coordenadas a mano; funciona igual.
        </p>
      ) : (
        alElegir && (
          <p className="mt-2 text-label-sm text-on-surface-variant">
            Toca el mapa para marcar la casa.
          </p>
        )
      )}
    </div>
  );
}
